import React from 'react';

import { LicenseModel } from '../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../utils/async';
import { licensePermissionChecks } from '../../utils/permission-check/license';
import { isDefined } from '../../utils/variables';
import { BuyDesignLicenseDialog } from '../components/buy-design-license-dialog/buy-design-license-dialog';
import { DesignLicenseAddedInfoDialog } from '../components/design-license-added-info-dialog/design-license-added-info-dialog';
import { DesignLicenseExpiredDialog } from '../components/design-license-expired-dialog/design-license-expired-dialog';

/**
 * Hook that fetches the current user's licenses and provides:
 * - a guard function to check whether a company has valid licenses
 * - dialog rendering for the expired-license flow (info → buy → confirmation)
 *
 * Use in list views (project list, company list) to intercept row clicks
 * before navigation when all licenses for the target company are expired.
 */
const useExpiredLicenseGuard = () => {
  const [userLicenses, setUserLicenses] = React.useState<LicenseModel[]>();
  const [expiredLicense, setExpiredLicense] = React.useState<{
    license: LicenseModel;
    projectId?: string;
  }>();
  const [showBuyDialog, setShowBuyDialog] = React.useState(false);
  const [showLicenseAddedInfo, setShowLicenseAddedInfo] = React.useState(false);

  React.useEffect(() => {
    iiafe(async () => {
      try {
        const { data } = await SkyMapAxiosServiceFactory.instance
          .createUserServiceV0()
          .getLicensesForCurrentUser({
            query: {
              include: ['license.licenseDemoPeriods', 'license.company', 'license.company.country'],
            },
          });

        setUserLicenses(data);
      } catch {
        // Silently fail — the user can still navigate and the global 403 handler
        // will redirect if needed.
      }
    });
  }, []);

  const isCompanyLicenseValid = React.useCallback(
    (companyId: string | undefined) => {
      if (!companyId || !isDefined(userLicenses)) {
        return true;
      }

      return userLicenses
        .filter((license) => license.companyId === companyId)
        .some(licensePermissionChecks.isLicenseValid);
    },
    [userLicenses],
  );

  const getExpiredLicenseForCompany = React.useCallback(
    (companyId: string | undefined) => {
      if (!companyId || !isDefined(userLicenses)) {
        return undefined;
      }

      return userLicenses
        .filter((license) => license.companyId === companyId)
        .find((x) => !licensePermissionChecks.isLicenseValid(x));
    },
    [userLicenses],
  );

  const showExpiredDialog = React.useCallback(
    (companyId: string, projectId?: string) => {
      const expired = getExpiredLicenseForCompany(companyId);
      if (expired) {
        setExpiredLicense({ license: expired, projectId });
      }
    },
    [getExpiredLicenseForCompany],
  );

  const renderExpiredLicenseDialog = React.useCallback(() => {
    if (!expiredLicense) {
      return null;
    }

    if (showLicenseAddedInfo) {
      return <DesignLicenseAddedInfoDialog license={expiredLicense.license} />;
    }

    if (showBuyDialog) {
      return (
        <BuyDesignLicenseDialog
          license={expiredLicense.license}
          projectId={expiredLicense.projectId ?? ''}
          onClose={() => setShowBuyDialog(false)}
          onFormSubmitted={() => {
            setShowBuyDialog(false);
            setShowLicenseAddedInfo(true);
          }}
        />
      );
    }

    return (
      <DesignLicenseExpiredDialog
        expiredLicense={expiredLicense.license}
        onBuyButtonClicked={() => setShowBuyDialog(true)}
        onClose={() => {
          setExpiredLicense(undefined);
          setShowBuyDialog(false);
          setShowLicenseAddedInfo(false);
        }}
      />
    );
  }, [expiredLicense, showBuyDialog, showLicenseAddedInfo]);

  return {
    isCompanyLicenseValid,
    showExpiredDialog,
    renderExpiredLicenseDialog,
  };
};

export { useExpiredLicenseGuard };
